import { useMemo, useState } from "react";
import { Layers, Sparkles, ArrowRight } from "lucide-react";
import { AGENTS } from "@/data/agents";
import { SECTORS } from "@/data/sectors";
import { useI18n } from "@/contexts/I18nContext";
import AgentIcon from "@/components/AgentIcon";
import AgentDetailModal from "@/components/AgentDetailModal";

// Deterministic mock activity/tokens per agent id (until real telemetry is wired).
function mockTelemetry(agent, sectorId) {
  const seed = (agent.name + (sectorId || "all")).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const activity = 35 + (seed % 60); // 35..95 %
  const tokensM = Math.round((1 + (seed % 9)) * 0.7 * 10) / 10; // 0.7..6.4 M
  return { activity, tokensM };
}

export default function EscouadeHud() {
  const { t, lang, formatPrice } = useI18n();
  const [sectorId, setSectorId] = useState("all");
  const [active, setActive] = useState(null);

  const highlighted = useMemo(() => {
    if (sectorId === "all") return new Set(AGENTS.map((a) => a.id));
    const s = SECTORS.find((x) => x.id === sectorId);
    return new Set(s?.pack || []);
  }, [sectorId]);

  return (
    <section
      data-testid="escouade-hud"
      id="escouade"
      className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16"
    >
      {/* Header */}
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="dncm-pulse-dot" />
            <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
              {lang === "en" ? "The Squad — 09 units" : "L'Escouade — 09 unités"}
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            {lang === "en" ? (
              <>
                Your AI <span className="dncm-gradient-text">arsenal</span>.
              </>
            ) : (
              <>
                Votre arsenal <span className="dncm-gradient-text">IA</span>.
              </>
            )}
          </h2>
          <p className="mt-3 text-white/65 max-w-xl">
            {lang === "en"
              ? "Pick your sector — the recommended agents instantly light up. Click any card to test it."
              : "Choisissez votre secteur — les agents recommandés s'illuminent. Cliquez sur une carte pour tester."}
          </p>
        </div>
        <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />
          Selena Matrix
        </div>
      </header>

      {/* Sector picker */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="sector-picker">
        <SectorChip
          id="all"
          label={lang === "en" ? "All agents" : "Tous"}
          active={sectorId === "all"}
          onClick={() => setSectorId("all")}
          neutral
        />
        {SECTORS.map((s) => (
          <SectorChip
            key={s.id}
            id={s.id}
            label={lang === "en" ? s.nameEn : s.name}
            active={sectorId === s.id}
            onClick={() => setSectorId(s.id)}
          />
        ))}
      </div>

      {/* HUD grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-5">
        {AGENTS.map((a, idx) => {
          const isLit = highlighted.has(a.id);
          return (
            <AgentHudCard
              key={a.id}
              agent={a}
              isLit={isLit}
              sectorId={sectorId}
              index={idx}
              onOpen={() => setActive(a)}
              priceLabel={formatPrice(a.price_php)}
            />
          );
        })}
      </div>

      {/* Footnote */}
      <div className="mt-6 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45 inline-flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />
        {lang === "en"
          ? "Avatars are placeholders — final portraits land on activation."
          : "Avatars provisoires — les portraits définitifs arrivent à l'activation."}
      </div>

      <AgentDetailModal agent={active} open={!!active} onOpenChange={(o) => !o && setActive(null)} />
    </section>
  );
}

function SectorChip({ label, active, onClick, neutral, id }) {
  return (
    <button
      data-testid={`sector-chip-${id}`}
      onClick={onClick}
      className={`px-3.5 py-2 rounded-full font-mono-d text-[11px] uppercase tracking-[0.2em] border transition-all ${
        active
          ? neutral
            ? "border-white/30 bg-white/8 text-white"
            : "border-[color:var(--dncm-green)]/80 bg-[color:var(--dncm-green-soft)] text-[color:var(--dncm-green)]"
          : "border-white/10 bg-black/40 text-white/60 hover:text-white hover:border-white/30"
      }`}
      style={
        active && !neutral
          ? { boxShadow: "0 0 22px rgba(222,255,154,0.45), inset 0 0 8px rgba(222,255,154,0.18)" }
          : undefined
      }
    >
      {label}
    </button>
  );
}

function AgentHudCard({ agent, isLit, sectorId, index, onOpen, priceLabel }) {
  const { lang, t } = useI18n();
  const tele = mockTelemetry(agent, sectorId);
  const lit = isLit; // boolean
  const litColor = "var(--dncm-green)";

  return (
    <button
      data-testid={`hud-agent-${agent.id}`}
      onClick={onOpen}
      className={`group relative text-left dncm-fadein rounded-lg p-3 transition-all ${
        lit ? "" : "opacity-40 grayscale-[0.2]"
      }`}
      style={{
        animationDelay: `${0.04 * index}s`,
        background: lit
          ? "radial-gradient(120% 90% at 50% 0%, rgba(222,255,154,0.10) 0%, rgba(5,5,5,0.0) 60%), rgba(8,10,12,0.85)"
          : "rgba(8,10,12,0.55)",
        border: `1px solid ${lit ? "rgba(222,255,154,0.65)" : "rgba(255,255,255,0.08)"}`,
        boxShadow: lit
          ? "0 0 36px rgba(222,255,154,0.35), inset 0 0 0 1px rgba(0,0,0,0.7)"
          : "inset 0 0 0 1px rgba(0,0,0,0.5)",
      }}
    >
      {/* HUD bracket corners */}
      <CornerBrackets lit={lit} />

      {/* Portrait placeholder */}
      <div
        className="relative h-40 w-full overflow-hidden rounded-md"
        style={{
          background: `radial-gradient(120% 90% at 50% 15%, ${
            lit ? "rgba(222,255,154,0.20)" : agent.color + "33"
          } 0%, rgba(5,5,5,0) 55%), #060708`,
        }}
      >
        {/* scanlines */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 3px)",
          }}
        />
        {/* big initial */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-brand"
            style={{
              fontSize: 96,
              lineHeight: 1,
              color: lit ? litColor : agent.color,
              textShadow: `0 0 18px ${lit ? "rgba(222,255,154,0.85)" : agent.color + "99"}, 0 0 48px ${
                lit ? "rgba(222,255,154,0.45)" : agent.color + "55"
              }`,
              letterSpacing: "0.04em",
              opacity: 0.95,
            }}
          >
            {agent.initial || agent.name[0]}
          </span>
        </div>
        {/* role icon */}
        <span
          className="absolute top-3 left-3 flex items-center justify-center w-8 h-8 rounded-md"
          style={{
            background: lit ? "rgba(222,255,154,0.18)" : `${agent.color}22`,
            border: `1px solid ${lit ? "rgba(222,255,154,0.7)" : agent.color + "55"}`,
            color: lit ? litColor : agent.color,
            boxShadow: lit ? "0 0 18px rgba(222,255,154,0.65)" : `0 0 14px ${agent.color}55`,
          }}
        >
          <AgentIcon name={agent.icon} className="w-4 h-4" />
        </span>
        {/* placeholder badge */}
        <span
          className="absolute top-3 right-3 px-2 py-0.5 rounded-full font-mono-d text-[9px] uppercase tracking-[0.22em]"
          style={{
            background: "rgba(0,0,0,0.55)",
            color: lit ? litColor : "rgba(255,255,255,0.55)",
            border: `1px solid ${lit ? "rgba(222,255,154,0.5)" : "rgba(255,255,255,0.12)"}`,
          }}
        >
          {lang === "en" ? "Placeholder" : "Avatar"}
        </span>
      </div>

      {/* Name + role */}
      <div className="mt-3 flex items-center justify-between">
        <div>
          <div className="font-display text-lg text-white leading-none">
            {agent.name.toUpperCase()}{" "}
            <span className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55 ml-1">
              {agent.role}
            </span>
          </div>
        </div>
        <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
          {priceLabel}
        </div>
      </div>

      {/* Gauges */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Gauge
          testid={`hud-${agent.id}-activity`}
          label={lang === "en" ? "Activity" : "Activité"}
          value={`${tele.activity}%`}
          pct={tele.activity}
          color={lit ? "var(--dncm-green)" : agent.color}
        />
        <Gauge
          testid={`hud-${agent.id}-tokens`}
          label={lang === "en" ? "Tokens" : "Tokens"}
          value={`${tele.tokensM.toFixed(1)}M`}
          pct={Math.min(100, tele.tokensM * 14)}
          color={lit ? "var(--dncm-green)" : "#7df9ff"}
        />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
          {lang === "en" ? "Click to open" : "Cliquer pour ouvrir"}
        </span>
        <ArrowRight
          className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1"
          style={{ color: lit ? litColor : "rgba(255,255,255,0.5)" }}
        />
      </div>
    </button>
  );
}

function CornerBrackets({ lit }) {
  const c = lit ? "var(--dncm-green)" : "rgba(0,207,255,0.55)";
  const size = 10;
  const common = {
    position: "absolute",
    width: size,
    height: size,
    borderColor: c,
    borderStyle: "solid",
    pointerEvents: "none",
  };
  return (
    <>
      <span aria-hidden="true" style={{ ...common, top: -1, left: -1, borderTopWidth: 1, borderLeftWidth: 1, borderRightWidth: 0, borderBottomWidth: 0 }} />
      <span aria-hidden="true" style={{ ...common, top: -1, right: -1, borderTopWidth: 1, borderRightWidth: 1, borderLeftWidth: 0, borderBottomWidth: 0 }} />
      <span aria-hidden="true" style={{ ...common, bottom: -1, left: -1, borderBottomWidth: 1, borderLeftWidth: 1, borderRightWidth: 0, borderTopWidth: 0 }} />
      <span aria-hidden="true" style={{ ...common, bottom: -1, right: -1, borderBottomWidth: 1, borderRightWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 }} />
    </>
  );
}

function Gauge({ label, value, pct, color, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-white/10 bg-black/40 px-3 py-2">
      <div className="flex items-center justify-between font-mono-d text-[9px] uppercase tracking-[0.22em] text-white/50">
        <span>{label}</span>
        <span className="text-white">{value}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full overflow-hidden bg-white/5 border border-white/10">
        <div
          className="h-full"
          style={{
            width: `${Math.max(4, Math.min(100, pct))}%`,
            background: color,
            boxShadow: `0 0 10px ${color}`,
            transition: "width .6s ease",
          }}
        />
      </div>
    </div>
  );
}
